<!-- packages/studio/src/components/army/UnitDetailForm.vue -->
<template>
  <div class="unit-detail">
    <div class="flags">
      <StCheckbox
        :model-value="modelValue.legends ?? false"
        @update:model-value="patch({ legends: $event })"
      >
        Legends
      </StCheckbox>
      <StCheckbox
        :model-value="modelValue.isWarmaster"
        @update:model-value="patch({ isWarmaster: $event })"
      >
        Warmaster
      </StCheckbox>
      <StCheckbox
        :model-value="modelValue.canBeReinforced"
        @update:model-value="patch({ canBeReinforced: $event })"
      >
        Can be reinforced
      </StCheckbox>
    </div>

    <div class="detail-grid">
      <StField label="Type">
        <StSelect
          :model-value="modelValue.type"
          @update:model-value="patch({ type: Number($event) })"
        >
          <option v-for="o in unitTypeOptions" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>
        </StSelect>
      </StField>
      <StField label="Unit size" hint="Models per unit">
        <StNumberInput :model-value="unitSize" :min="1" @update:model-value="setUnitSize($event)" />
      </StField>
      <StField label="Base size">
        <StTextInput
          :model-value="modelValue.models[0]?.baseSize ?? ''"
          @update:model-value="setBaseSize($event ?? '')"
        />
      </StField>
    </div>

    <StField group label="Keywords">
      <!-- Keywords derived from other fields are shown as locked chips and can't
           be edited here — they always reflect the army, type, and flags. -->
      <ul v-if="derivedKeywords.length" class="derived">
        <li v-for="kw in derivedKeywords" :key="kw" class="derived__chip" title="Auto-filled">
          <FontAwesomeIcon icon="lock" class="derived__lock" />
          {{ kw }}
        </li>
      </ul>
      <KeywordsInput
        raw
        uppercase
        suggestion-scope="unit"
        placeholder="e.g. WIZARD, FLY"
        :model-value="modelValue.keywords"
        @update:model-value="patch({ keywords: $event })"
      />
    </StField>

    <StField group label="Allowed enhancements" hint="Enhancement tables this unit may choose from">
      <!-- Free-text editor so any table can be added by name (studio is used for
           patching — the army may already carry tables studio doesn't model). -->
      <KeywordsInput
        raw
        noun="Enhancement"
        suggestion-scope="enhancement"
        placeholder="e.g. Monstrous Actions"
        :model-value="allowedEnhancements"
        @update:model-value="setEnhancements"
      />
      <!-- Convenience quick-adds for the common + army-defined tables. -->
      <div v-if="addableEnhancements.length" class="enh__quick">
        <span class="enh__quick-label">Quick add:</span>
        <StButton
          v-for="cat in addableEnhancements"
          :key="cat"
          size="sm"
          variant="secondary"
          icon="plus"
          @click="addEnhancement(cat)"
        >
          {{ cat }}
        </StButton>
      </div>
    </StField>

    <section class="form-section">
      <div class="st-eyebrow">Rules</div>
      <EditableList
        :items="modelValue.rules"
        add-label="Add rule"
        remove-label="Remove rule"
        @add="addRule"
        @remove="removeRule"
      >
        <template #row="{ index }">
          <StField label="Name"
            ><StTextInput
              :model-value="modelValue.rules[index].name"
              @update:model-value="patchRule(index, { name: $event ?? '' })"
          /></StField>
          <StField label="Description"
            ><StTextarea
              :model-value="modelValue.rules[index].description"
              @update:model-value="patchRule(index, { description: $event ?? '' })"
          /></StField>
        </template>
      </EditableList>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IUnit, IRule, IEnhancementSlot } from '@scrolldex/shared';
import {
  UnitType,
  HEROIC_TRAITS,
  ARTEFACTS_OF_POWER,
  makeEnhancementSlot,
  type GrandAlliance,
} from '@scrolldex/shared';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EditableList from '../EditableList.vue';
import KeywordsInput from '../KeywordsInput.vue';
import { derivedUnitKeywords } from '../../utils/unitKeywords.js';
import {
  StField,
  StTextInput,
  StTextarea,
  StSelect,
  StNumberInput,
  StCheckbox,
  StButton,
} from '../ui/index.js';

const props = defineProps<{
  modelValue: IUnit;
  armyKeyword?: string;
  alliance?: GrandAlliance;
  // Enhancement category names the army offers (defaults + faction-specific).
  enhancementCategories?: string[];
}>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IUnit): void }>();

const unitTypeOptions = [
  { value: UnitType.Hero, label: 'Hero' },
  { value: UnitType.Infantry, label: 'Infantry' },
  { value: UnitType.Cavalry, label: 'Cavalry' },
  { value: UnitType.Beast, label: 'Beast' },
  { value: UnitType.Monster, label: 'Monster' },
  { value: UnitType.WarMachine, label: 'War Machine' },
  { value: UnitType.Manifestation, label: 'Manifestation' },
  { value: UnitType.Terrain, label: 'Terrain' },
];

// Unit size is the model count; v1 keeps a single squad size (min === max).
const unitSize = computed<number>(() => props.modelValue.models[0]?.min ?? 1);

// Keywords the warscroll gets automatically from other fields. These are
// display-only here — the merge/app derive the real ones the same way.
const derivedKeywords = computed<string[]>(() =>
  derivedUnitKeywords(props.modelValue, {
    armyKeyword: props.armyKeyword,
    alliance: props.alliance,
  }),
);

// Allowed enhancements: the tables this unit may choose from, keyed by table name.
// Addable categories come from the army (Heroic Traits / Artefacts of Power plus any
// faction-specific ones like Monstrous Traits); the two universal ones are always
// offered as a fallback when no army context is provided (e.g. regiment-of-renown
// units). Categories already on the unit show as removable chips.
const addableCategories = computed(() => {
  const cats = [...(props.enhancementCategories ?? [])];
  for (const c of [HEROIC_TRAITS, ARTEFACTS_OF_POWER]) if (!cats.includes(c)) cats.push(c);
  return cats;
});
const allowedEnhancements = computed(() => Object.keys(props.modelValue.enhancements ?? {}));
const addableEnhancements = computed(() =>
  addableCategories.value.filter((c) => !(c in (props.modelValue.enhancements ?? {}))),
);
// Rebuild the enhancements map from the edited name list, preserving any existing
// slot (its id and any filled upgrade) and minting a fresh slot for new names.
function setEnhancements(names: string[]) {
  const prev = props.modelValue.enhancements ?? {};
  const next: { [name: string]: IEnhancementSlot } = {};
  for (const name of names) next[name] = prev[name] ?? makeEnhancementSlot(name);
  patch({ enhancements: next });
}
function addEnhancement(name: string) {
  if (name in (props.modelValue.enhancements ?? {})) return;
  setEnhancements([...allowedEnhancements.value, name]);
}

function patch(part: Partial<IUnit>) {
  emit('update:modelValue', { ...props.modelValue, ...part });
}
function setUnitSize(size: number | null) {
  const n = Math.max(1, size ?? 1);
  const models = props.modelValue.models.map((m, i) => (i === 0 ? { ...m, min: n, max: n } : m));
  patch({ models });
}
function setBaseSize(size: string) {
  const models = props.modelValue.models.map((m, i) => (i === 0 ? { ...m, baseSize: size } : m));
  patch({ models });
}
function patchRule(index: number, part: Partial<IRule>) {
  patch({ rules: props.modelValue.rules.map((r, i) => (i === index ? { ...r, ...part } : r)) });
}
function addRule() {
  patch({ rules: [...props.modelValue.rules, { name: '', description: '' }] });
}
function removeRule(index: number) {
  patch({ rules: props.modelValue.rules.filter((_, i) => i !== index) });
}
</script>

<style scoped>
.unit-detail {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.flags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-4);
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--st-space-2);
}
.derived {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1);
}
.derived__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-1);
  padding: 2px var(--st-space-2);
  background: var(--st-surface-sunken);
  border: 1px dashed var(--st-border-strong);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
}
.derived__lock {
  font-size: var(--st-text-2xs);
  opacity: 0.7;
}
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}

/* Allowed-enhancement quick-add buttons (under the free-text editor). */
.enh__quick {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--st-space-2);
  margin-top: var(--st-space-2);
}
.enh__quick-label {
  font-size: var(--st-text-xs);
  color: var(--st-text-subtle);
}
</style>
